document.documentElement.classList.remove('no-js');

const liveRegion = document.getElementById('PuffsLiveRegion');

function announce(message) {
  if (!liveRegion) return;
  liveRegion.textContent = '';
  window.setTimeout(() => { liveRegion.textContent = message; }, 50);
}

class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.drawer = this.querySelector('[data-menu-drawer]');
    this.backdrop = this.querySelector('[data-menu-backdrop]');
    if (!this.toggle || !this.drawer) return;
    this.toggle.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-menu-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });
  }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.backdrop?.setAttribute('data-open', 'true');
    document.body.classList.add('menu-open');
    this.drawer.querySelector('button, a')?.focus();
  }

  close() {
    if (this.drawer.getAttribute('aria-hidden') === 'true') return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.backdrop?.setAttribute('data-open', 'false');
    document.body.classList.remove('menu-open');
    this.toggle.focus();
  }
}

class SearchDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.querySelector('[data-search-open]')?.addEventListener('click', () => {
      this.dialog?.showModal();
      this.dialog?.querySelector('input')?.focus();
    });
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.dialog?.close());
    this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
  }
}

class PuffsContactDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-contact-dialog]');
    this.form = this.querySelector('[data-contact-form]');
    this.subject = this.form?.elements.subject;
    this.message = this.form?.elements.message;
    if (!this.dialog || !this.form) return;

    this.onDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);
    this.querySelectorAll('[data-contact-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('close', () => this.afterClose());
    this.form.addEventListener('submit', (event) => this.openEmailApp(event));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest('[data-contact-open]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger);
  }

  open(trigger) {
    this.previousFocus = trigger || document.activeElement;
    if (!this.dialog.open) this.dialog.showModal();
    document.body.classList.add('contact-dialog-open');
    window.requestAnimationFrame(() => this.subject?.focus());
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  afterClose() {
    document.body.classList.remove('contact-dialog-open');
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    this.previousFocus = null;
  }

  openEmailApp(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;
    const subject = encodeURIComponent(this.subject.value.trim());
    const body = encodeURIComponent(this.message.value.trim());
    this.close();
    window.location.href = `mailto:puffscollects@gmail.com?subject=${subject}&body=${body}`;
  }
}

// AJAX add-to-cart: one delegated listener for every form that posts to /cart/add.
// Progressive enhancement — the forms still work as a plain POST when JS is off.
const CartAjax = {
  root: window.Shopify?.routes?.root || '/',

  init() {
    // Single document-level listener (capture) — no per-form listeners, no timers.
    document.addEventListener('submit', (event) => this.onSubmit(event));
  },

  isCartAdd(form) {
    if (!form || form.tagName !== 'FORM') return false;
    const action = form.getAttribute('action') || form.action || '';
    return /\/cart\/add/.test(action);
  },

  onSubmit(event) {
    const form = event.target;
    if (!this.isCartAdd(form)) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"], button:not([type])');
    if (button && button.disabled) return; // already in flight
    this.addItem(form, button);
  },

  async addItem(form, button) {
    const original = button ? button.textContent.trim() : '';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-adding');
    }
    this.clearFeedback(form);
    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This one could not be added.');
      await this.refreshCount();
      announce(`${data.product_title || 'Your squishy'} tucked into the bag.`);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      this.showAdded(button, original);
    } catch (error) {
      announce(error.message);
      this.showFeedback(form, error.message);
      this.restoreButton(button, original);
    }
  },

  async refreshCount(retry = true) {
    try {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const cart = JSON.parse(await response.text()); // JSON.parse throws on a non-JSON (e.g. HTML) body
      if (typeof cart.item_count === 'number') this.updateCount(cart.item_count);
    } catch (error) {
      // A transient non-JSON response must never be stored as the count. Retry once, then
      // leave the badge as-is rather than showing a wrong number.
      if (retry) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
        return this.refreshCount(false);
      }
    }
  },

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count <= 0;
      if (count > 0) {
        badge.classList.remove('is-bumped');
        void badge.offsetWidth; // restart the bump animation
        badge.classList.add('is-bumped');
      }
    });
  },

  showAdded(button, original) {
    if (!button) return;
    button.classList.remove('is-adding');
    button.classList.add('is-added');
    button.textContent = 'Added ✓';
    window.setTimeout(() => this.restoreButton(button, original), 1800);
  },

  restoreButton(button, original) {
    if (!button) return;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-adding', 'is-added');
    if (original) button.textContent = original;
  },

  showFeedback(form, message) {
    let note = form.querySelector('[data-cart-feedback]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'cart-feedback';
      note.setAttribute('data-cart-feedback', '');
      note.setAttribute('role', 'status');
      form.appendChild(note);
    }
    note.textContent = message;
    window.clearTimeout(note._timer);
    note._timer = window.setTimeout(() => this.clearFeedback(form), 4000);
  },

  clearFeedback(form) {
    const note = form.querySelector('[data-cart-feedback]');
    if (note) {
      window.clearTimeout(note._timer);
      note.remove();
    }
  }
};

CartAjax.init();

class PuffsProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.openButtons = Array.from(this.querySelectorAll('[data-gallery-open]'));
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    if (!this.dialog || !this.openButtons.length || !this.slides.length) return;

    this.closeButton = this.querySelector('[data-gallery-close]');
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.currentLabel = this.querySelector('[data-gallery-current]');
    this.captionLabel = this.querySelector('[data-gallery-caption]');
    this.zoomOutButton = this.querySelector('[data-gallery-zoom-out]');
    this.zoomInButton = this.querySelector('[data-gallery-zoom-in]');
    this.zoomResetButton = this.querySelector('[data-gallery-zoom-reset]');
    this.zoomValue = this.querySelector('[data-gallery-zoom-value]');
    this.activeIndex = 0;
    this.touchStart = null;
    this.zoom = 1;
    this.minimumZoom = 1;
    this.maximumZoom = 3;
    this.zoomStep = .5;
    this.panX = 0;
    this.panY = 0;
    this.dragPointer = null;

    this.slides.forEach((slide) => {
      const image = slide.querySelector('img');
      if (image) image.draggable = false;
    });

    this.openButtons.forEach((button) => {
      button.addEventListener('click', () => this.open(Number(button.dataset.galleryIndex), button));
    });
    this.closeButton?.addEventListener('click', () => this.close());
    this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('close', () => this.afterClose());
    this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
    this.dialog.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
    this.dialog.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
    this.zoomOutButton?.addEventListener('click', () => this.setZoom(this.zoom - this.zoomStep));
    this.zoomInButton?.addEventListener('click', () => this.setZoom(this.zoom + this.zoomStep));
    this.zoomResetButton?.addEventListener('click', () => this.resetZoom());
    this.viewport?.addEventListener('dblclick', (event) => this.onDoubleClick(event));
    this.viewport?.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.viewport?.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.viewport?.addEventListener('pointerup', (event) => this.onPointerEnd(event));
    this.viewport?.addEventListener('pointercancel', (event) => this.onPointerEnd(event));
    if ('ResizeObserver' in window && this.viewport) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.zoom > this.minimumZoom) {
          this.clampPan();
          this.applyTransform();
        }
      });
      this.resizeObserver.observe(this.viewport);
    }
    this.updateZoomControls();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  open(index, trigger) {
    this.previousFocus = trigger || document.activeElement;
    this.goTo(index);
    if (!this.dialog.open) this.dialog.showModal();
    document.body.classList.add('lightbox-open');
    window.requestAnimationFrame(() => this.closeButton?.focus());
  }

  close() {
    this.resetZoom();
    if (this.dialog.open) this.dialog.close();
  }

  afterClose() {
    this.resetZoom();
    document.body.classList.remove('lightbox-open');
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    this.previousFocus = null;
  }

  goTo(index) {
    this.resetZoom();
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== this.activeIndex;
    });
    if (this.currentLabel) this.currentLabel.textContent = String(this.activeIndex + 1);
    if (this.captionLabel) this.captionLabel.textContent = this.slides[this.activeIndex].dataset.galleryCaptionValue || '';
  }

  activeImage() {
    return this.slides[this.activeIndex]?.querySelector('img') || null;
  }

  setZoom(value) {
    const nextZoom = Math.min(this.maximumZoom, Math.max(this.minimumZoom, value));
    this.zoom = Math.round(nextZoom * 100) / 100;
    if (this.zoom === this.minimumZoom) {
      this.panX = 0;
      this.panY = 0;
      this.endDrag();
    } else {
      this.clampPan();
    }
    this.applyTransform();
    this.updateZoomControls();
  }

  resetZoom() {
    this.zoom = this.minimumZoom;
    this.panX = 0;
    this.panY = 0;
    this.endDrag();
    this.applyTransform();
    this.updateZoomControls();
  }

  applyTransform() {
    const image = this.activeImage();
    if (image) {
      image.style.transform = this.zoom === this.minimumZoom
        ? ''
        : `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.zoom})`;
    }
    this.viewport?.classList.toggle('is-zoomed', this.zoom > this.minimumZoom);
  }

  updateZoomControls() {
    if (this.zoomValue) this.zoomValue.textContent = `${Math.round(this.zoom * 100)}%`;
    if (this.zoomOutButton) this.zoomOutButton.disabled = this.zoom <= this.minimumZoom;
    if (this.zoomInButton) this.zoomInButton.disabled = this.zoom >= this.maximumZoom;
    if (this.zoomResetButton) this.zoomResetButton.disabled = this.zoom === this.minimumZoom && this.panX === 0 && this.panY === 0;
  }

  clampPan() {
    const slide = this.slides[this.activeIndex];
    const image = this.activeImage();
    if (!slide || !image) return;
    const maxX = Math.max(0, (image.offsetWidth * this.zoom - slide.clientWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * this.zoom - slide.clientHeight) / 2);
    this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
    this.panY = Math.min(maxY, Math.max(-maxY, this.panY));
  }

  onDoubleClick(event) {
    if (event.target !== this.activeImage()) return;
    event.preventDefault();
    if (this.zoom > this.minimumZoom) this.resetZoom();
    else this.setZoom(2);
  }

  onPointerDown(event) {
    if (this.zoom <= this.minimumZoom || event.button !== 0 || event.target !== this.activeImage()) return;
    event.preventDefault();
    this.dragPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    this.viewport?.setPointerCapture(event.pointerId);
    this.viewport?.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (!this.dragPointer || event.pointerId !== this.dragPointer.id) return;
    event.preventDefault();
    this.panX += event.clientX - this.dragPointer.x;
    this.panY += event.clientY - this.dragPointer.y;
    this.dragPointer.x = event.clientX;
    this.dragPointer.y = event.clientY;
    this.clampPan();
    this.applyTransform();
  }

  onPointerEnd(event) {
    if (!this.dragPointer || event.pointerId !== this.dragPointer.id) return;
    this.endDrag();
  }

  endDrag() {
    if (this.dragPointer && this.viewport?.hasPointerCapture(this.dragPointer.id)) {
      this.viewport.releasePointerCapture(this.dragPointer.id);
    }
    this.dragPointer = null;
    this.viewport?.classList.remove('is-dragging');
  }

  onKeydown(event) {
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.setZoom(this.zoom + this.zoomStep);
    } else if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      this.setZoom(this.zoom - this.zoomStep);
    } else if (event.key === '0') {
      event.preventDefault();
      this.resetZoom();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.activeIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.activeIndex + 1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  onTouchStart(event) {
    if (this.zoom > this.minimumZoom) {
      this.touchStart = null;
      return;
    }
    if (event.touches.length !== 1) return;
    this.touchStart = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY
    };
  }

  onTouchEnd(event) {
    if (this.zoom > this.minimumZoom) {
      this.touchStart = null;
      return;
    }
    if (!this.touchStart || event.changedTouches.length !== 1 || this.slides.length < 2) return;
    const deltaX = event.changedTouches[0].clientX - this.touchStart.x;
    const deltaY = event.changedTouches[0].clientY - this.touchStart.y;
    this.touchStart = null;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.goTo(this.activeIndex + (deltaX < 0 ? 1 : -1));
  }
}

customElements.define('mobile-menu', MobileMenu);
customElements.define('search-dialog', SearchDialog);
customElements.define('puffs-contact-dialog', PuffsContactDialog);
customElements.define('puffs-product-gallery', PuffsProductGallery);
